import React, { Component } from "react";
import Card from "react-bootstrap/Card";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import Table from "react-bootstrap/Table";
import Badge from "react-bootstrap/Badge";
import { Spinner } from "react-bootstrap";
import { withRouter } from "../withRouter";
import { getProjects, getMergeSummary, mergeEvents } from "../api";
import AdminNavBar from "./AdminNavBar";
import LoadingButton from "./LoadingButton";
import { Shuffle, InfoCircle, CheckCircle } from "react-bootstrap-icons";

class MergeEvents extends Component {
  state = {
    projects: [],
    selectedEvents: [],
    mergedProjectName: "",
    loading: true,
    summary: null,
    showSummary: false,
    merging: false,
    error: null,
    success: null,
  };

  async componentDidMount() {
    try {
      const projects = await getProjects();
      this.setState({ projects, loading: false });
    } catch (error) {
      this.setState({
        error: "Erreur lors du chargement des projets",
        loading: false,
      });
    }
  }

  handleEventSelection = (eventId) => {
    const { selectedEvents } = this.state;
    const eventIdNum = parseInt(eventId);

    if (selectedEvents.includes(eventIdNum)) {
      this.setState({
        selectedEvents: selectedEvents.filter((id) => id !== eventIdNum),
        showSummary: false,
        summary: null,
      });
    } else {
      this.setState({
        selectedEvents: [...selectedEvents, eventIdNum],
        showSummary: false,
        summary: null,
      });
    }
  };

  handlePreview = async () => {
    const { selectedEvents, mergedProjectName } = this.state;

    if (selectedEvents.length < 2) {
      this.setState({
        error: "Veuillez sélectionner au moins 2 events à fusionner",
      });
      return;
    }

    if (!mergedProjectName.trim()) {
      this.setState({
        error: "Veuillez entrer un nom pour le projet fusionné",
      });
      return;
    }

    this.setState({ loading: true, error: null });

    try {
      const summary = await getMergeSummary(selectedEvents, mergedProjectName);
      this.setState({
        summary,
        showSummary: true,
        loading: false,
      });
    } catch (error) {
      this.setState({
        error: "Erreur lors de la génération du résumé",
        loading: false,
      });
    }
  };

  handleMerge = async () => {
    const { selectedEvents, mergedProjectName } = this.state;

    this.setState({ merging: true, error: null });

    try {
      const result = await mergeEvents(selectedEvents, mergedProjectName);
      this.setState({
        success: result.message,
        merging: false,
      });

      // Redirection après 2 secondes
      setTimeout(() => {
        this.props.navigate(
          `/admin/project/${result.project_id}/event/${result.event_id}`
        );
      }, 2000);
    } catch (error) {
      this.setState({
        error: "Erreur lors de la fusion des events",
        merging: false,
      });
    }
  };

  render() {
    const {
      projects,
      selectedEvents,
      mergedProjectName,
      loading,
      summary,
      showSummary,
      merging,
      error,
      success,
    } = this.state;

    if (loading && !summary) {
      return (
        <div style={{ backgroundColor: "#F6F7F9", minHeight: "100vh" }}>
          <AdminNavBar />
          <Container fluid className="px-5 pt-3 pb-5">
            <div className="text-center my-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Chargement...</span>
              </Spinner>
            </div>
          </Container>
        </div>
      );
    }

    return (
      <div style={{ backgroundColor: "#F6F7F9", minHeight: "100vh" }}>
        <AdminNavBar />
        <Container fluid className="px-5 pt-3 pb-5">
          {/* Header */}
          <div
            className="border-bottom mb-3"
            style={{
              flexDirection: "row",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                flexDirection: "row",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Shuffle size={26} />
              <h2 className="ms-3">Fusionner des Events</h2>
            </div>
          </div>

          {/* Alerts */}
          {error && (
            <Alert variant="danger" onClose={() => this.setState({ error: null })} dismissible>
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success">
              <CheckCircle className="me-2" />
              {success}
              <p className="mb-0 mt-2">Redirection en cours...</p>
            </Alert>
          )}

          {/* Configuration */}
          {!showSummary && !success && (
            <>
              {/* Configuration Section */}
              <div
                className="border-bottom mb-3"
                style={{
                  flexDirection: "row",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <InfoCircle size={26} />
                <h3 className="ms-3" style={{ padding: 0, margin: 0 }}>
                  Configuration de la fusion
                </h3>
              </div>

              <Card className="mb-4" style={{ backgroundColor: "white" }}>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Nom du projet fusionné *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Ex: DELPHI Asthme sévère 2026 (Fusionné)"
                      value={mergedProjectName}
                      onChange={(e) =>
                        this.setState({ mergedProjectName: e.target.value })
                      }
                    />
                    <Form.Text className="text-muted">
                      Un nouveau projet sera créé avec ce nom
                    </Form.Text>
                  </Form.Group>
                </Card.Body>
              </Card>

              {/* Sélection des events */}
              <div
                className="border-bottom mb-3 mt-3"
                style={{
                  flexDirection: "row",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Shuffle size={26} />
                <h3 className="ms-3" style={{ padding: 0, margin: 0 }}>
                  Sélection des Events (minimum 2)
                </h3>
              </div>

              <Card className="mb-4" style={{ backgroundColor: "white" }}>
                <Card.Body>
                  {projects.map((project) => (
                    <Card key={project.id} className="mb-3">
                      <Card.Header>
                        <strong>{project.name}</strong>
                        <Badge bg="secondary" className="ms-2">
                          {project.events.length} event(s)
                        </Badge>
                      </Card.Header>
                      <Card.Body>
                        {project.events.length === 0 ? (
                          <p className="text-muted mb-0">Aucun event disponible</p>
                        ) : (
                          project.events.map((event) => (
                            <Form.Check
                              key={event.id}
                              type="checkbox"
                              id={`event-${event.id}`}
                              className="mb-2"
                              label={
                                <span>
                                  <strong>{event.name}</strong>
                                  <br />
                                  <small className="text-muted">
                                    {new Date(event.start_date).toLocaleDateString()} -{" "}
                                    {new Date(event.end_date).toLocaleDateString()} |{" "}
                                    Status: {event.status}
                                  </small>
                                </span>
                              }
                              checked={selectedEvents.includes(event.id)}
                              onChange={() => this.handleEventSelection(event.id)}
                            />
                          ))
                        )}
                      </Card.Body>
                    </Card>
                  ))}
                </Card.Body>
              </Card>

              {/* Bouton Prévisualiser */}
              <div className="d-flex gap-3 mb-4">
                <LoadingButton
                  variant="primary"
                  onClick={this.handlePreview}
                  disabled={selectedEvents.length < 2 || !mergedProjectName.trim()}
                >
                  Prévisualiser la fusion
                </LoadingButton>
                <p className="text-muted mt-2">
                  {selectedEvents.length} event(s) sélectionné(s)
                </p>
              </div>
            </>
          )}

          {/* Résumé de la fusion */}
          {showSummary && summary && !success && (
            <>
              <div
                className="border-bottom mb-3"
                style={{
                  flexDirection: "row",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <CheckCircle size={26} />
                <h3 className="ms-3" style={{ padding: 0, margin: 0 }}>
                  Résumé de la fusion
                </h3>
              </div>

              <Card className="mb-4" style={{ backgroundColor: "white" }}>
                <Card.Body>
                  <h6 className="mb-3">Events sources:</h6>
                  <Table striped bordered size="sm" className="mb-4">
                    <thead>
                      <tr>
                        <th>Event</th>
                        <th>Projet</th>
                        <th>Dates</th>
                        <th>Votants</th>
                        <th>Statements</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.source_events.map((event) => (
                        <tr key={event.id}>
                          <td>{event.name}</td>
                          <td>{event.project_name}</td>
                          <td>
                            <small>
                              {new Date(event.start_date).toLocaleDateString()} -{" "}
                              {new Date(event.end_date).toLocaleDateString()}
                            </small>
                          </td>
                          <td>{event.voters_count}</td>
                          <td>{event.statements_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  <h6 className="mb-3">Résultat de la fusion:</h6>
                  <Table bordered className="mb-0">
                    <tbody>
                      <tr>
                        <td>
                          <strong>Statements</strong>
                        </td>
                        <td>
                          <div className="mb-1">
                            <small className="text-muted">Avant fusion:</small>{" "}
                            {summary.total_statements_before_merge}
                          </div>
                          <div className="mb-1">
                            <Badge bg="warning" text="dark">{summary.duplicate_statements_merged} doublons éliminés</Badge>
                          </div>
                          <div>
                            <Badge bg="success">{summary.unique_statements_kept} statements uniques conservés</Badge>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Questions</strong>
                        </td>
                        <td>
                          <div className="mb-1">
                            <small className="text-muted">Avant fusion:</small>{" "}
                            {summary.total_questions_before_merge}
                          </div>
                          <div className="mb-1">
                            <Badge bg="warning" text="dark">{summary.duplicate_questions_merged} doublons éliminés</Badge>
                          </div>
                          <div>
                            <Badge bg="success">{summary.unique_questions_kept} questions uniques conservées</Badge>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Votants totaux</strong>
                        </td>
                        <td colSpan="2">{summary.total_voters}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Votes totaux</strong>
                        </td>
                        <td colSpan="2">{summary.total_votes}</td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              {/* Boutons d'action */}
              <div className="d-flex gap-3 mb-4">
                <LoadingButton
                  variant="success"
                  onClick={this.handleMerge}
                  loading={merging}
                  disabled={merging}
                >
                  {merging ? "Fusion en cours..." : "Confirmer la fusion"}
                </LoadingButton>
                <Button
                  variant="secondary"
                  onClick={() => this.setState({ showSummary: false, summary: null })}
                  disabled={merging}
                >
                  Retour
                </Button>
              </div>

              <Alert variant="info">
                <InfoCircle className="me-2" />
                <strong>Note:</strong> La fusion créera un nouveau projet "{mergedProjectName}
                " avec tous les statements, questions, votes et réponses fusionnés. Les
                exports et graphiques fonctionneront normalement sur l'event fusionné.
              </Alert>
            </>
          )}
        </Container>
      </div>
    );
  }
}

export default withRouter(MergeEvents);
